import { useDocumentTitle } from "@uidotdev/usehooks";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/components/Auth";
import ErrorText from "@/components/ErrorText";
import Loader from "@/components/Loader";
import { useNotification } from "@/components/Notification";
import { PageTitle } from "@/constants/pages";
import { useStaffClientOneTimeCodeMutation } from "@/hooks/user";

type UiState =
  | {
      type: "loading";
    }
  | {
      type: "error";
    };

export default function StaffClientOneTimeCodePage() {
  const [searchParams] = useSearchParams();
  const showNotification = useNotification();
  const { signInWithToken, isSignedIn, logout } = useAuth();
  const [state, setState] = useState<UiState>({ type: "loading" });
  const code = searchParams.get("code");
  const loadingRef = useRef(false);

  const exchangeStaffClientOneTimeCodeMutation =
    useStaffClientOneTimeCodeMutation();

  const handleSubmit = async (code: string) => {
    if (loadingRef.current) {
      return;
    }
    try {
      loadingRef.current = true;
      const result = await exchangeStaffClientOneTimeCodeMutation.mutateAsync({
        code,
      });
      const { jwt, redirect_url, missingRequiredFields } = result;

      if (redirect_url) {
        localStorage.setItem("redirect", redirect_url);
      }
      showNotification(`You are now signed in`);
      signInWithToken({
        res: {
          token: jwt,
          missingRequiredFields: missingRequiredFields,
        },
        action: null,
      });
    } catch (_) {
      setState({ type: "error" });
    } finally {
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    async function handler() {
      if (code && !exchangeStaffClientOneTimeCodeMutation.isPending) {
        await handleSubmit(code);
      } else {
        setState({ type: "error" });
      }
    }

    // noinspection JSIgnoredPromiseFromCall
    void handler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    if (isSignedIn) {
      logout();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDocumentTitle(PageTitle.STAFF_CLIENT_ONE_TIME_CODE);

  if (state.type === "error") {
    return (
      <div>
        <ErrorText className={"text-center"}>
          There was a problem, please talk to the staff.
        </ErrorText>
      </div>
    );
  }

  return (
    <div>
      <Loader />
    </div>
  );
}
